import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Crown, Droplets, MapPin, Palette, Phone, Shield, TrendingUp } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import ConsultationForm from "@/components/ConsultationForm";
import FloorVisualizer from "@/components/FloorVisualizer";
import { useContent } from "@/context/ContentProvider";

const REASON_ICONS = [Palette, Shield, Droplets, TrendingUp];

export default function Home() {
  const location = useLocation();
  const { texts, services, gallery, phones, phoneLinks, heroImage } = useContent();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useEffect(() => {
    if (location.state?.scrollTo) {
      const t = setTimeout(() => {
        document.getElementById(location.state.scrollTo)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [location.state]);

  return (
    <div>
      <Seo
        title="Crownvera Epoxy | Epoxy Floor Coatings in Cleveland, OH"
        description="Crownvera Epoxy transforms garages, basements, patios & workshops with metallic epoxy, decorative flake & polished concrete in Cleveland, OH. 440-381-7828"
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={heroImage} alt="White and gold liquid epoxy floor swirl" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-50/70 via-stone-50/40 to-stone-50/10 md:bg-gradient-to-r md:from-stone-50/60 md:via-stone-50/25 md:to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#FAF8F5] to-transparent" />
        </motion.div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pb-24 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-amber-800 mb-6 [text-shadow:0_1px_10px_rgba(250,248,245,0.9)]"
            data-testid="hero-eyebrow"
          >
            <MapPin className="w-4 h-4" /> {texts.heroEyebrow}
          </motion.p>

          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-stone-900 leading-[1.08] max-w-4xl [text-shadow:0_2px_18px_rgba(250,248,245,0.95)]"
            data-testid="hero-headline"
          >
            {texts.heroHeadline.split(" ").map((word, i) => (
              <span key={`${word}-${i}`} className="inline-block overflow-hidden pb-1 align-bottom">
                <motion.span
                  className="inline-block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.25 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                >
                  {word}&nbsp;
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-stone-800 font-medium leading-relaxed [text-shadow:0_1px_12px_rgba(250,248,245,0.95)]"
            data-testid="hero-subcopy"
          >
            {texts.heroSubcopy}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/services"
              data-testid="hero-services-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-sm px-8 py-4 rounded-md shadow-md transition-colors duration-300 flex items-center gap-2"
            >
              Explore Services <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-8 flex flex-wrap gap-x-8 gap-y-2 text-sm text-stone-800 font-semibold"
          >
            {phones.map((p, i) => (
              <a key={p} href={phoneLinks[i]} data-testid={`hero-phone-${i + 1}`} className="flex items-center gap-2 hover:text-amber-800 transition-colors duration-300 [text-shadow:0_1px_10px_rgba(250,248,245,0.9)]">
                <Phone className="w-4 h-4 text-amber-800" /> {p}
              </a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-amber-600/15 bg-[#F5F2EB] py-5 overflow-hidden" data-testid="services-marquee" aria-hidden="true">
        <div className="flex w-max animate-marquee">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center shrink-0">
              {[...services.map((s) => s.name), "Free In-Home Consultations"].map((item) => (
                <span key={`${copy}-${item}`} className="flex items-center">
                  <span className="font-display text-lg sm:text-xl text-stone-500 italic px-8 whitespace-nowrap">{item}</span>
                  <Crown className="w-4 h-4 text-amber-600/60 shrink-0" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* WHY EPOXY */}
      <section id="why-epoxy" className="max-w-7xl mx-auto px-4 sm:px-8 py-24 sm:py-32" data-testid="why-epoxy-section">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-700 mb-4">Why Epoxy</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-stone-900 max-w-2xl leading-tight">
            {texts.whyHeading}
          </h2>
          <p className="mt-5 max-w-2xl text-base text-stone-600 leading-relaxed">{texts.whyIntro}</p>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {(texts.whyCards || []).map((card, i) => {
            const Icon = REASON_ICONS[i % REASON_ICONS.length];
            return (
              <Reveal key={card.title} delay={i * 0.1}>
                <div className="gold-ring-card rounded-lg p-7 h-full" data-testid={`why-card-${i}`}>
                  <Icon className="w-8 h-8 text-amber-700 mb-5" />
                  <h3 className="font-display text-xl text-amber-900 mb-3">{card.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed">{card.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FLOOR VISUALIZER */}
      <section id="visualizer" className="max-w-7xl mx-auto px-4 sm:px-8 pb-24 sm:pb-32" data-testid="visualizer-section">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-700 mb-4">AI Floor Preview</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-stone-900 leading-tight">
              Want to see what your floors could look like?
            </h2>
            <p className="mt-5 max-w-lg text-base text-stone-600 leading-relaxed">
              Snap a photo of your floor, pick a finish and color, and our AI will show you the transformation in seconds — your room, your lighting, your new floor. Then we'll bring real samples to your free in-home consultation.
            </p>
            <p className="mt-6 flex items-center gap-2 text-sm font-semibold text-amber-800">
              <Shield className="w-4 h-4" /> Every finish backed by our 10-year warranty
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <FloorVisualizer />
            <p className="mt-5 text-sm text-stone-600 leading-relaxed text-center" data-testid="visualizer-custom-note">
              <span className="font-semibold text-stone-800">Don't see the finish you have in mind?</span>
              <br />
              Crownvera offers custom colors and finishes beyond the options shown here. Call us or schedule a consultation to create something unique for your space.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="relative py-24 sm:py-32 overflow-hidden bg-white" data-testid="services-section">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-700 mb-4">Our Craft</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-stone-900">
                Eight systems. One crown standard.
              </h2>
            </div>
            <Link to="/services" data-testid="services-view-all-link" className="text-amber-700 hover:text-amber-800 text-sm font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors duration-300">
              All services <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 4) * 0.08}>
                <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="gold-ring-card group block rounded-lg overflow-hidden">
                  <div className="h-44 overflow-hidden">
                    <img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg text-amber-900 mb-2">{s.name}</h3>
                    <p className="text-sm text-stone-500 leading-relaxed line-clamp-2">{s.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY TEASER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-24" data-testid="gallery-teaser-section">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-700 mb-4">Recent Work</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-stone-900">
              Floors that start conversations
            </h2>
          </div>
          <Link to="/gallery" data-testid="gallery-view-all-link" className="text-amber-700 hover:text-amber-800 text-sm font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors duration-300">
            Full gallery <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {gallery.slice(0, 4).map((g, i) => (
            <Reveal key={g.title} delay={i * 0.08}>
              <Link to="/gallery" data-testid={`gallery-teaser-${i}`} className="group block relative rounded-lg overflow-hidden border border-amber-600/15 shadow-sm">
                <img src={g.url} alt={g.title} loading="lazy" className="w-full h-56 sm:h-64 object-cover transition-transform duration-700 group-hover:scale-108" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <p className="text-xs text-amber-300 uppercase tracking-wider">{g.category}</p>
                  <p className="text-sm text-white mt-1">{g.title}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-amber-600/15 bg-[#F5F2EB]" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {[
            ["Locally Owned", "Born & raised in Cleveland"],
            ["Free Consultations", "We come to your home"],
            ["10-Year Warranty", "On every finish we pour"],
            ["Northeast Ohio", "Proudly serving the region"],
          ].map(([title, sub], i) => (
            <Reveal key={title} delay={i * 0.08}>
              <p className="font-display text-xl text-amber-900" data-testid={`trust-item-${i}`}>{title}</p>
              <p className="text-sm text-stone-500 mt-2">{sub}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CONSULTATION */}
      <section id="consultation" className="max-w-7xl mx-auto px-4 sm:px-8 py-24 sm:py-32" data-testid="consultation-section">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-700 mb-4">Free In-Home Consultation</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-stone-900 leading-tight">
              {texts.consultHeading}
            </h2>
            <p className="mt-5 text-base text-stone-600 leading-relaxed max-w-lg">{texts.consultText}</p>
            <div className="mt-8 space-y-3">
              {phones.map((p, i) => (
                <a key={p} href={phoneLinks[i]} data-testid={`consultation-phone-${i + 1}`} className="flex items-center gap-3 text-lg text-stone-800 hover:text-amber-700 transition-colors duration-300">
                  <Phone className="w-5 h-5 text-amber-700" /> {p}
                </a>
              ))}
            </div>
            <p className="mt-6 flex items-center gap-2 text-sm font-semibold text-amber-800" data-testid="consultation-warranty-badge">
              <Shield className="w-4 h-4" /> 10-year warranty included on every finish
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="gold-ring-card rounded-xl p-7 sm:p-9">
              <ConsultationForm formId="section-consultation" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
